import { Controller } from "@hotwired/stimulus"
import { stimulus } from "../init"

export default class InstitutionScanner extends Controller {
  static targets = ["video", "status", "form", "attendeeId", "eventId"]
  static values = { scanUrl: String }

  async start() {
    if (!("BarcodeDetector" in window)) {
      this.statusTarget.textContent = "QR camera scanning is not supported in this browser. Use manual check-in."
      return
    }

    try {
      this.stream = await navigator.mediaDevices.getUserMedia({ video: { facingMode: "environment" } })
      this.videoTarget.srcObject = this.stream
      this.detector = new window.BarcodeDetector({ formats: ["qr_code"] })
      this.statusTarget.textContent = "Camera ready. Point it at an attendee ticket."
      this.scanFrame()
    } catch {
      this.statusTarget.textContent = "Camera access failed. Check browser permissions or use manual check-in."
    }
  }

  stop() {
    this.stream?.getTracks().forEach((track) => track.stop())
    this.stream = null
    this.videoTarget.srcObject = null
    this.statusTarget.textContent = "Camera stopped."
  }

  async submit(event) {
    event.preventDefault()
    await this.checkIn(this.attendeeIdTarget.value, this.eventIdTarget.value)
  }

  async scanFrame() {
    if (!this.stream) return

    try {
      const [result] = await this.detector.detect(this.videoTarget)
      const match = result?.rawValue.match(/(?:\/attendee-verification\/verify\/|^https?:\/\/[^/]+\/)([^/?#]+)\/([^/?#]+)/)
      if (match) {
        this.attendeeIdTarget.value = match[1]
        this.eventIdTarget.value = match[2]
        await this.checkIn(match[1], match[2])
        return
      }
    } catch {
      this.statusTarget.textContent = "Could not read that code. Hold the ticket steady and try again."
    }

    this.frame = window.requestAnimationFrame(() => this.scanFrame())
  }

  async checkIn(attendeeId, eventId) {
    this.statusTarget.textContent = "Checking in..."
    const retrying = this.pendingScan?.attendeeId === attendeeId && this.pendingScan?.eventId === eventId
    if (!retrying) this.pendingScan = { attendeeId, eventId, key: window.crypto.randomUUID() }

    try {
      const response = await fetch(this.scanUrlValue, {
        method: "POST",
        credentials: "same-origin",
        headers: {
          "Content-Type": "application/json",
          "Accept": "application/json",
          "X-CSRF-Token": document.querySelector("meta[name='csrf-token']")?.content
        },
        body: JSON.stringify({ attendee_id: attendeeId, event_id: eventId, idempotency_key: this.pendingScan.key })
      })
      const payload = await response.json()
      this.statusTarget.textContent = payload.message
      this.pendingScan = null
      if (response.ok) window.location.reload()
    } catch {
      this.statusTarget.textContent = "Check-in failed. Verify the connection and try again."
    }
  }

  disconnect() {
    this.stop()
    if (this.frame) window.cancelAnimationFrame(this.frame)
  }
}

stimulus.register("institution-scanner", InstitutionScanner)
